/**
 * Pure helpers for "Documents folder focus" (drill-down) in the sidebar.
 *
 * Contract: path segments are folder **names** from the loaded tree root, e.g.
 * ['clients', 'greater-good']. Files are not valid focus targets.
 */

export type DocumentsTreeNode = {
  id: string
  name: string
  type: 'folder' | 'file'
  children?: DocumentsTreeNode[]
}

/** Minimal shape for name-walk / drill-down (FolderTreeNode satisfies this). */
export type NameTreeNode = {
  name: string
  type: 'folder' | 'file'
  children?: NameTreeNode[]
}

export function getFolderAtPathGeneric<T extends NameTreeNode>(nodes: T[], pathSegments: string[]): T | null {
  if (pathSegments.length === 0) return null
  const [first, ...rest] = pathSegments
  const child = nodes.find(n => n.name === first && n.type === 'folder') as T | undefined
  if (!child) return null
  if (rest.length === 0) return child
  return child.children ? getFolderAtPathGeneric(child.children as T[], rest) : null
}

export function sanitizeTreeFocusPath<T extends NameTreeNode>(nodes: T[], segments: string[]): string[] {
  const out: string[] = []
  let level: T[] = nodes
  for (const seg of segments) {
    const n = level.find(x => x.name === seg && x.type === 'folder')
    if (!n) break
    out.push(seg)
    level = (n.children ?? []) as T[]
  }
  return out
}

export function getTreeDrillDownView<T extends NameTreeNode>(
  rootNodes: T[],
  focusPathSegments: string[],
): { displayRoots: T[]; pathPrefix: string[] } {
  if (focusPathSegments.length === 0) {
    return { displayRoots: rootNodes, pathPrefix: [] }
  }
  const focused = getFolderAtPathGeneric(rootNodes, focusPathSegments)
  if (focused == null) {
    return { displayRoots: rootNodes, pathPrefix: focusPathSegments }
  }
  return {
    displayRoots: (focused.children ?? []) as T[],
    pathPrefix: focusPathSegments,
  }
}

/** @deprecated use getFolderAtPathGeneric — kept for call sites on DocumentsTreeNode */
export function getFolderAtPath(nodes: DocumentsTreeNode[], pathSegments: string[]): DocumentsTreeNode | null {
  return getFolderAtPathGeneric(nodes, pathSegments)
}

export function sanitizeDocumentsFocusPath(nodes: DocumentsTreeNode[], segments: string[]): string[] {
  return sanitizeTreeFocusPath(nodes, segments)
}

export type DocumentsDrillDownView = {
  displayRoots: DocumentsTreeNode[]
  pathPrefix: string[]
}

export function getDocumentsDrillDownView(
  rootNodes: DocumentsTreeNode[],
  focusPathSegments: string[],
): DocumentsDrillDownView {
  return getTreeDrillDownView(rootNodes, focusPathSegments)
}
